import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { api, SessionExpired, type Meta, type Ticket } from './api';
import { POLL_MS } from './freshness';

export interface Board {
  tickets: Ticket[];
  meta: Meta | null;
  loading: boolean;
  error: string | null;
  /** When the ticket list was last successfully fetched — null until the first load. */
  lastUpdated: number | null;
  /**
  * When the in-flight background poll started, or null if none is. A timestamp rather
  * than a boolean so the UI can bound how long "refreshing" is allowed to suppress the
  * staleness signal — see `isSettling`.
  */
  refreshingSince: number | null;
  reload: () => Promise<void>;
  /** Applies a server-returned ticket in place, so a save does not re-fetch the world. */
  apply: (ticket: Ticket) => void;
}

/**
 * The whole board is loaded once and filtered in memory — ~90 tickets is a couple of
 * hundred kilobytes, and it is what makes grouping, sorting and (later) search instant
 * without any server-side query surface.
 */
/**
 * Runs `refresh` on an interval, but only while the tab is visible — a tab left open
 * overnight would otherwise be ~2,800 pointless invocations. Returning refreshes
 * immediately, so the board is already correct by the time you have looked at it.
 *
 * `visibilitychange` covers the common case (other tab, minimised); `focus` covers
 * returning to the window without a visibility transition.
 */
function usePolling(
  signedIn: boolean,
  refresh: () => Promise<void>,
  lastUpdatedRef: MutableRefObject<number>,
) {
  useEffect(() => {
    if (!signedIn) return;

    const tick = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    let timer = window.setInterval(tick, POLL_MS);

    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      // `inFlight` only dedupes CONCURRENT requests. This is bound to both
      // `visibilitychange` and `focus`, so without a floor, alt-tabbing between the
      // browser and a terminal — the normal shape of a ship loop — would turn the cost
      // from one fetch per 30s into one per focus. Returning after three seconds is not
      // staleness.
      if (Date.now() - lastUpdatedRef.current < POLL_MS) return;
      void refresh();
      // Restart the interval so the first poll after waking is a full period away,
      // rather than however much of the old period happened to be left.
      window.clearInterval(timer);
      timer = window.setInterval(tick, POLL_MS);
    };

    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [signedIn, refresh, lastUpdatedRef]);
}

export function useBoard(signedIn: boolean): Board {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [refreshingSince, setRefreshingSince] = useState<number | null>(null);
  // Refs, not state, for the two values the polling effect reads: putting either in the
  // effect's dependency array would tear down and rebuild the interval on every poll.
  const inFlight = useRef(false);
  const lastUpdatedRef = useRef(0);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [board, values] = await Promise.all([api.list(), api.meta()]);
      setTickets(board.tickets);
      setMeta(values);
      setLastUpdated(Date.now());
      lastUpdatedRef.current = Date.now();
    } catch (cause) {
      // A session expiry is already handled globally by AuthProvider; surfacing it as a
      // board error too would put a scary red banner behind the sign-in prompt.
      if (!(cause instanceof SessionExpired)) {
        setError(cause instanceof Error ? cause.message : 'Could not load the board.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * The background refresh, and deliberately NOT `reload`.
   *
   * `reload` raises `loading`, which makes App render a spinner INSTEAD of the board — on
   * a timer that is a full-board flicker every tick. It also clears and re-sets `error`,
   * so one dropped request would replace a working board with a red banner.
   *
   * So this touches neither. A failure keeps the last good data and stays quiet; the next
   * tick retries, and `lastUpdated` going stale on screen is what makes a poller that has
   * genuinely stopped visible. Silence plus a fresh-looking board would be the worst of
   * both — staleness that reads as freshness.
   *
   * Only the list: `/v1/meta` is enums and users, which change when the code changes.
   */
  const refresh = useCallback(async () => {
    if (inFlight.current) return; // A slow response must not let ticks stack up behind it.
    inFlight.current = true;
    setRefreshingSince(Date.now());
    try {
      const board = await api.list();
      // Merged, not replaced, for the reason `apply` gives: the list endpoint omits
      // bodies, so a wholesale replace every 30s would quietly undo the invariant `apply`
      // goes out of its way to hold. Nothing reads a body off this array today; the first
      // view that does would otherwise blank it on a timer, with no obvious cause.
      setTickets((current) =>
        board.tickets.map((incoming) => {
          const held = current.find((ticket) => ticket.ref === incoming.ref);
          return held ? { ...held, ...incoming } : incoming;
        }),
      );
      setLastUpdated(Date.now());
      lastUpdatedRef.current = Date.now();
    } catch {
      // Kept quiet on purpose — see above. Session expiry is NOT swallowed by this:
      // api.ts dispatches `dwlf-session-expired` before it throws (api.ts:150), so
      // AuthProvider still reacts even though the error dies here.
    } finally {
      inFlight.current = false;
      setRefreshingSince(null);
    }
  }, []);

  useEffect(() => {
    if (signedIn) void reload();
  }, [signedIn, reload]);


  usePolling(signedIn, refresh, lastUpdatedRef);

  const apply = useCallback((updated: Ticket) => {
    setTickets((current) => {
      const index = current.findIndex((t) => t.ref === updated.ref);
      if (index === -1) return [...current, updated].sort((a, b) => a.ref - b.ref);
      const next = [...current];
      // The list endpoint omits bodies; keep whatever body we already hold so a field
      // edit from the table view does not blank the body in local state.
      next[index] = { ...next[index], ...updated };
      return next;
    });
  }, []);

  return { tickets, meta, loading, error, lastUpdated, refreshingSince, reload, apply };
}
